/**
 * Destination differee, en attente d'une connexion.
 *
 * Un appui sur une notification doit ouvrir l'ecran concerne. Or l'application
 * se relance systematiquement deconnectee — useAutoDisconnect revoque la session
 * a chaque demarrage a froid — et l'utilisateur passe donc par le login, ce qui
 * ferait perdre la destination.
 *
 * Elle est conservee dans le sessionStorage : la duree de vie d'une session de
 * webview correspond exactement a celle du besoin, et rien ne subsiste si
 * l'utilisateur ferme l'application sans se connecter.
 */
const STORAGE_KEY = 'pendingRedirect';

/**
 * N'accepte qu'un chemin interne.
 *
 * Sans ce controle, une valeur venue de l'URL permettrait de rediriger vers un
 * domaine tiers apres connexion (open redirect). Le double slash est rejete car
 * `//exemple.com` est une URL protocole-relative, pas un chemin.
 */
const isInternalPath = (path: unknown): path is string =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');

export const setPendingRedirect = (path: unknown): void => {
    if (!isInternalPath(path)) {
        return;
    }

    try {
        sessionStorage.setItem(STORAGE_KEY, path);
    } catch {
        // Stockage indisponible : la redirection est perdue, sans plus.
    }
};

/**
 * Recupere la destination du parametre `redirect` de l'URL courante, le cas
 * echeant. C'est la voie utilisee quand le shell natif ouvre la webview sur une
 * URL construite, plutot qu'en injectant la route apres coup.
 */
export const capturePendingRedirectFromUrl = (): void => {
    try {
        const value = new URLSearchParams(window.location.search).get('redirect');

        if (value) {
            setPendingRedirect(value);
        }
    } catch {
        // URL illisible : rien a capturer.
    }
};

/**
 * Renvoie la destination en attente et l'efface. Un appel unique, pour eviter
 * qu'une redirection ne se rejoue a une connexion ulterieure.
 */
export const consumePendingRedirect = (): string | null => {
    try {
        const value = sessionStorage.getItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);

        return isInternalPath(value) ? value : null;
    } catch {
        return null;
    }
};
